import { Image } from 'expo-image'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import amexIcon from '@/assets/payment/amex.svg'
import mastercardIcon from '@/assets/payment/mastercard.svg'
import paypalIcon from '@/assets/payment/paypal.png'
import sepaIcon from '@/assets/payment/sepa.png'
import visaIcon from '@/assets/payment/visa.svg'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useLocaleParts } from '@/hooks/useLocaleParts'
import { reportError } from '@/services/RUM'
import { Checkbox as Chk } from '@/shared/components/flows/Checkbox'
import { trpc } from '@/utils/trpc'
import type { PremiumInterval, PriceData } from '@flows-ui/services/QuoteDataContext.types'
import {
  buildAddPaymentInfoPayload,
  extractTrackingFlowValues,
  getTariffLine,
} from '@flows-ui/utils/tracking'
import { EVENTS } from '@flows/configs/analytics/constants'
import { type Stripe, type StripeCardNumberElement, loadStripe } from '@stripe/stripe-js'
import { useMutation, useQuery } from '@tanstack/react-query'
import Alert from '@ui-kit/components/Alert'
import { COLOR } from '@ui-kit/constants'
import { body1, body2, caption } from '@ui-kit/core/typography'

type PaymentType = 'SEPA' | 'CREDIT_CARD' | 'PAYPAL'

type Props = {
  submitHandler: () => void | Promise<void>
  flowName: string
  productKey?: string
  priceData?: PriceData
  premiumInterval?: PremiumInterval
  permanentDiscountLost?: boolean
}

const accordionStyles = tv({
  slots: {
    container: 'mb-3',
    button: 'flex-row items-center justify-between rounded-xl border-2 bg-white p-4',
    label: body1({ bold: true, color: 'deepForest' }),
    icons: 'flex-row items-center gap-1',
    icon: 'h-6 w-10',
    content: 'mt-1 rounded-xl bg-grey-100 p-5',
  },
  variants: {
    selected: {
      true: { button: 'border-deep-forest' },
      false: { button: 'border-grey-200' },
    },
  },
})

const floatingInputStyles = tv({
  slots: {
    container: 'relative mb-2 rounded-lg border bg-white px-3 pb-2 pt-5',
    label: caption({ color: 'grey400', className: 'absolute left-3 top-1.5' }),
    input: 'w-full border-none bg-transparent p-0 outline-none',
  },
  variants: {
    disabled: {
      true: { container: 'border-grey-200' },
      false: { container: 'border-black' },
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const sepaStyles = tv({
  slots: {
    submitButton: 'mt-1 w-full items-center rounded-lg py-3.5',
  },
  variants: {
    hasIban: {
      true: { submitButton: 'bg-black' },
      false: { submitButton: 'bg-grey-300' },
    },
  },
})

const cardStyles = tv({
  slots: {
    field: 'h-14 rounded-xl border border-grey-300 bg-white px-4',
    fieldSpaced: 'mb-3 h-14 rounded-xl border border-grey-300 bg-white px-4',
    fieldRow: 'mb-3 flex gap-3',
    fieldFlex: 'h-14 flex-1 rounded-xl border border-grey-300 bg-white px-4',
    disclaimer: caption({ color: 'grey400', className: 'my-2 mb-3' }),
    loading: body2({ color: 'grey400', className: 'text-center' }),
  },
})

const paymentStyles = tv({
  slots: {
    errorBanner: 'mb-4 rounded-lg bg-error-light p-3',
    errorText: body2({ color: 'error' }),
    successBanner: 'mb-4 rounded-lg bg-success-light p-3',
    successText: body2({ color: 'success' }),
    info: body2({ color: 'grey500', className: 'mb-4' }),
    infoShort: body2({ color: 'grey500', className: 'mb-3' }),
    loadingText: body2({ color: 'grey400' }),
    errorDetailText: body2({ color: 'error' }),
    discountWarning: 'mb-4',
    mountPoint: 'min-h-[48px]',
    unsupported: caption({ color: 'grey400', className: 'mt-2' }),
  },
})

const PaymentAccordion = ({
  label,
  icons,
  selected,
  onPress,
  children,
}: {
  label: string
  icons: { s: { src: string } | string; alt: string }[]
  selected: boolean
  onPress: () => void
  children?: React.ReactNode
}) => {
  const styles = accordionStyles({ selected })

  return (
    <View className={styles.container()}>
      <Pressable onPress={onPress} className={styles.button()}>
        <Text className={styles.label()}>{label}</Text>
        <View className={styles.icons()}>
          {icons.map((i) => (
            <Image
              key={i.alt}
              source={typeof i.s === 'string' ? i.s : i.s.src}
              alt={i.alt}
              contentFit="contain"
              className={styles.icon()}
            />
          ))}
        </View>
      </Pressable>
      {selected && children ? <View className={styles.content()}>{children}</View> : null}
    </View>
  )
}

const FloatingInput = ({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string
  value: string
  onChange?: (v: string) => void
  disabled?: boolean
  type?: string
}) => {
  const styles = floatingInputStyles({ disabled: Boolean(disabled) })

  return (
    <View className={styles.container()}>
      <Text className={styles.label()}>{label}</Text>
      <input
        aria-label={label}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        disabled={disabled}
        type={type}
        className={body1({
          color: disabled ? 'grey500' : 'deepForest',
          className: styles.input(),
        })}
      />
    </View>
  )
}

const stringOrEmpty = z.string().catch('')

const parentPolicyHolderSchema = z.object({
  first_name: stringOrEmpty,
  last_name: stringOrEmpty,
  email: stringOrEmpty,
  street: stringOrEmpty,
  houseNumber: stringOrEmpty,
  house_no: stringOrEmpty,
  zip_code: stringOrEmpty,
  town: stringOrEmpty,
})

const readParentPolicyHolder = (parent: unknown) => {
  const p = parentPolicyHolderSchema.parse(parent)
  return {
    firstName: p.first_name,
    lastName: p.last_name,
    email: p.email,
    street: p.street,
    houseNo: p.houseNumber || p.house_no,
    zipCode: p.zip_code,
    town: p.town,
  }
}

type SepaFormValues = {
  same: boolean
  firstName: string
  lastName: string
  email: string
  street: string
  houseNo: string
  zipCode: string
  town: string
  iban: string
}

const SepaForm = ({
  onSuccess,
  onError,
  flowName,
}: {
  onSuccess: () => void
  onError: (k: string) => void
  flowName: string
}) => {
  const { t } = useTranslation()
  const { language, market } = useLocaleParts()
  const { watch: watchParent } = useFormContext()
  const mutation = trpc.paymentFlow.applyDirectDebit.useMutation()

  const parentFields = readParentPolicyHolder(watchParent())

  const { control, handleSubmit, watch, formState } = useForm<SepaFormValues>({
    defaultValues: {
      same: true,
      ...parentFields,
      iban: '',
    },
  })

  const same = watch('same')
  const iban = watch('iban')
  const { isSubmitting } = formState

  const onSubmit = handleSubmit(async (data) => {
    if (!data.iban) {
      onError('sharedFlows_common:errors.invalidIBAN')
      return
    }
    const pick = (localVal: string, parentVal: string) => (data.same ? parentVal : localVal)
    const result = await mutation.mutateAsync({
      language,
      market,
      flowName,
      iban: data.iban,
      accountHolderSameAsPolicy: data.same,
      firstName: pick(data.firstName, parentFields.firstName),
      lastName: pick(data.lastName, parentFields.lastName),
      email: pick(data.email, parentFields.email),
      street: pick(data.street, parentFields.street),
      houseNo: pick(data.houseNo, parentFields.houseNo),
      zip: pick(data.zipCode, parentFields.zipCode),
      town: pick(data.town, parentFields.town),
    })
    if (!result.success) {
      onError(('errorKey' in result && result.errorKey) || 'sharedFlows_common:errors.common')
      return
    }
    onSuccess()
  })

  const styles = sepaStyles({ hasIban: Boolean(iban) })

  return (
    <View>
      <Controller
        control={control}
        name="same"
        render={({ field }) => (
          <Chk checked={field.value} onPress={() => field.onChange(!field.value)}>
            {t('sharedFlows_common:steps.payment.sepa.account_holder_same')}
          </Chk>
        )}
      />
      <Controller
        control={control}
        name="firstName"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.email_and_name.firstName.input.label')}
            value={same ? parentFields.firstName : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
          />
        )}
      />
      <Controller
        control={control}
        name="lastName"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.email_and_name.lastName.input.label')}
            value={same ? parentFields.lastName : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.email_and_name.email.input.label')}
            value={same ? parentFields.email : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
            type="email"
          />
        )}
      />
      <Controller
        control={control}
        name="street"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.location.inputs.street')}
            value={same ? parentFields.street : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
          />
        )}
      />
      <Controller
        control={control}
        name="houseNo"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.location.inputs.house')}
            value={same ? parentFields.houseNo : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
          />
        )}
      />
      <Controller
        control={control}
        name="zipCode"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.location.inputs.plz')}
            value={same ? parentFields.zipCode : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
            type="number"
          />
        )}
      />
      <Controller
        control={control}
        name="town"
        render={({ field }) => (
          <FloatingInput
            label={t('sharedFlows_common:steps.location.inputs.location')}
            value={same ? parentFields.town : field.value}
            onChange={same ? undefined : field.onChange}
            disabled={same}
          />
        )}
      />
      <FloatingInput
        label={t('sharedFlows_common:steps.location.inputs.country')}
        value={t('sharedFlows_common:countries.de')}
        disabled
      />

      <Controller
        control={control}
        name="iban"
        render={({ field }) => (
          <FloatingInput
            label="IBAN"
            value={field.value}
            onChange={(v) => field.onChange(v.toUpperCase())}
          />
        )}
      />
      <Pressable
        onPress={() => {
          onSubmit().catch(reportError)
        }}
        disabled={isSubmitting || !iban}
        className={styles.submitButton()}
      >
        <Text className={body1({ bold: true, color: iban ? 'white' : 'grey500' })}>
          {isSubmitting
            ? t('sharedFlows_common:steps.payment.loading')
            : t('sharedFlows_common:steps.payment.sepa.apply_payment_data_button_label')}
        </Text>
      </Pressable>
    </View>
  )
}

type StripeState = {
  stripe: Stripe | null
  cardNumber: StripeCardNumberElement | null
  clientSecret: string | null
}

const CreditCardForm = ({
  stripeKey,
  onSuccess,
  onError,
  loading,
  setLoading,
  flowName,
}: {
  stripeKey: string | null
  onSuccess: () => void
  onError: (k: string) => void
  loading: boolean
  setLoading: (v: boolean) => void
  flowName: string
}) => {
  const { t } = useTranslation()
  const { language, market } = useLocaleParts()
  const { watch } = useFormContext()
  const parentFields = readParentPolicyHolder(watch())
  const setupMutation = trpc.paymentFlow.getStripeSetupIntent.useMutation()

  const numRef = useRef<HTMLDivElement>(null)
  const expRef = useRef<HTMLDivElement>(null)
  const cvcRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<StripeState>({
    stripe: null,
    cardNumber: null,
    clientSecret: null,
  })
  const [mandate, setMandate] = useState(false)
  const [formStatus, setFormStatus] = useState<
    Record<string, { complete?: boolean; empty?: boolean }>
  >({
    number: { empty: true },
    expiry: { empty: true },
    cvc: { empty: true },
  })

  const stripeElementsRef = useRef<{ destroy: () => void }[]>([])

  useEffect(() => {
    if (!stripeKey || typeof window === 'undefined') {
      return
    }
    let off = false
    void (async () => {
      try {
        const stripe = await loadStripe(stripeKey)
        if (off || !stripe) {
          return
        }
        const el = stripe.elements()
        const style = {
          base: {
            fontSize: '16px',
            lineHeight: '48px',
            color: COLOR.deepForest,
            '::placeholder': { color: COLOR.grey400 },
          },
        }
        const cn = el.create('cardNumber', {
          style,
          showIcon: true,
          placeholder: '1234 1234 1234 1234',
        })
        const ce = el.create('cardExpiry', { style, placeholder: 'MM / YY' })
        const cc = el.create('cardCvc', { style, placeholder: 'CVC' })
        stripeElementsRef.current = [cn, ce, cc]
        cn.on('change', (e: { complete: boolean; empty: boolean }) =>
          setFormStatus((s) => ({ ...s, number: { complete: e.complete, empty: e.empty } }))
        )
        ce.on('change', (e: { complete: boolean; empty: boolean }) =>
          setFormStatus((s) => ({ ...s, expiry: { complete: e.complete, empty: e.empty } }))
        )
        cc.on('change', (e: { complete: boolean; empty: boolean }) =>
          setFormStatus((s) => ({ ...s, cvc: { complete: e.complete, empty: e.empty } }))
        )
        if (numRef.current) {
          cn.mount(numRef.current)
        }
        if (expRef.current) {
          ce.mount(expRef.current)
        }
        if (cvcRef.current) {
          cc.mount(cvcRef.current)
        }
        if (!off) {
          setState({ stripe, cardNumber: cn, clientSecret: null })
        }
      } catch (error) {
        reportError(error, { source: 'Stripe init' })
      }
    })()
    return () => {
      off = true
      for (const element of stripeElementsRef.current) {
        element.destroy()
      }
      stripeElementsRef.current = []
    }
  }, [stripeKey])

  const allFieldsComplete =
    formStatus.number?.complete &&
    formStatus.expiry?.complete &&
    formStatus.cvc?.complete &&
    mandate

  const confirmCardMutation = useMutation({
    mutationFn: async () => {
      if (!state.stripe || !state.cardNumber) {
        throw new Error('Stripe not ready')
      }
      const si = await setupMutation.mutateAsync({ language, market, flowName })
      const clientSecret = si?.stripe_client_secret ?? si?.client_secret
      if (!clientSecret) {
        throw new Error('No client secret')
      }
      const r = await state.stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: state.cardNumber,
          billing_details: {
            name: `${parentFields.firstName} ${parentFields.lastName}`.trim(),
            email: parentFields.email,
          },
        },
      })
      if (r.error) {
        throw new Error(r.error.message ?? 'Stripe card setup failed')
      }
    },
    onMutate: () => setLoading(true),
    onSettled: () => setLoading(false),
    onSuccess: () => onSuccess(),
    onError: () => onError('sharedFlows_common:errors.common'),
  })

  const {
    mutate: confirmCard,
    isPending: confirmPending,
    isSuccess: confirmSucceeded,
  } = confirmCardMutation
  const canConfirmCard = !confirmPending && !confirmSucceeded

  useEffect(() => {
    if (!allFieldsComplete || !state.stripe || !state.cardNumber || !canConfirmCard) {
      return
    }
    confirmCard()
  }, [allFieldsComplete, state.stripe, state.cardNumber, canConfirmCard, confirmCard])

  const styles = cardStyles()

  return (
    <View>
      <div ref={numRef} className={styles.fieldSpaced()} />
      <div className={styles.fieldRow()}>
        <div ref={expRef} className={styles.fieldFlex()} />
        <div ref={cvcRef} className={styles.fieldFlex()} />
      </div>
      <Chk checked={mandate} onPress={() => setMandate(!mandate)}>
        {t('sharedFlows_common:generic.inputs.credit_card_mandate')}
      </Chk>
      <Text className={styles.disclaimer()}>
        {t('sharedFlows_common:steps.payment.card.authorisationDisclaimer')}
      </Text>
      {loading ? (
        <Text className={styles.loading()}>{t('sharedFlows_common:steps.payment.loading')}</Text>
      ) : null}
    </View>
  )
}

const PayPalSection = ({
  onSuccess,
  onError,
  flowName,
}: {
  onSuccess: () => void
  onError: (k: string) => void
  flowName: string
}) => {
  const { t } = useTranslation()
  const { language, market } = useLocaleParts()
  const getTokenMutation = trpc.paymentFlow.getBraintreeToken.useMutation()
  const createMutation = trpc.paymentFlow.createPaypalPaymentMethod.useMutation()
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorDetail, setErrorDetail] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const initializedRef = useRef(false)

  // Refs so the mount effect captures latest callbacks without re-rendering PayPal buttons.
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const createMutationRef = useRef(createMutation)
  createMutationRef.current = createMutation

  const tokenQuery = useQuery({
    queryKey: ['paypal', 'braintreeToken', language, market, flowName],
    queryFn: async () => {
      const tokenData = await getTokenMutation.mutateAsync({ language, market, flowName })
      return tokenData?.client_token ?? null
    },
  })

  useEffect(() => {
    if (typeof window === 'undefined' || initializedRef.current) {
      return
    }
    if (tokenQuery.isPending) {
      return
    }
    if (tokenQuery.isError) {
      setErrorDetail(
        tokenQuery.error instanceof Error ? tokenQuery.error.message : String(tokenQuery.error)
      )
      setStatus('error')
      return
    }
    if (!tokenQuery.data) {
      setErrorDetail('No Braintree client token received')
      setStatus('error')
      return
    }

    initializedRef.current = true
    const clientToken = tokenQuery.data
    let cancelled = false

    void (async () => {
      try {
        const braintree = await import('braintree-web')
        const client = await braintree.client.create({ authorization: clientToken })
        const dataCollector = await braintree.dataCollector.create({ client })
        const paypalCheckout = await braintree.paypalCheckout.create({ client })
        await paypalCheckout.loadPayPalSDK({ vault: true, intent: 'tokenize' })
        if (cancelled) {
          return
        }
        const paypalSdk = window.paypal
        if (!paypalSdk?.Buttons) {
          setErrorDetail('PayPal SDK failed to load')
          setStatus('error')
          return
        }
        setStatus('ready')
        const buttons = paypalSdk.Buttons({
          fundingSource: paypalSdk.FUNDING?.PAYPAL ?? 'paypal',
          style: { shape: 'pill', color: 'gold', label: 'checkout', height: 48 },
          createBillingAgreement: () => paypalCheckout.createPayment({ flow: 'vault' }),
          onApprove: async (data: unknown) => {
            try {
              const payload = await paypalCheckout.tokenizePayment(data)
              await createMutationRef.current.mutateAsync({
                language,
                market,
                flowName,
                braintreeNonce: payload.nonce,
                deviceData: dataCollector.deviceData,
              })
              onSuccessRef.current()
            } catch (error) {
              reportError(error, { source: 'PayPal tokenize' })
              onErrorRef.current('sharedFlows_common:errors.common')
            }
          },
          onError: (err: unknown) => {
            reportError(err, { source: 'PayPal onError' })
            onErrorRef.current('sharedFlows_common:errors.common')
          },
        })
        if (containerRef.current) {
          buttons.render(containerRef.current).catch(reportError)
        }
      } catch (error: unknown) {
        reportError(error, { source: 'PayPal init' })
        if (!cancelled) {
          setErrorDetail(error instanceof Error ? error.message : String(error))
          setStatus('error')
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    tokenQuery.data,
    tokenQuery.isPending,
    tokenQuery.isError,
    tokenQuery.error,
    language,
    market,
    flowName,
  ])

  const styles = paymentStyles()

  return (
    <View>
      <Text className={styles.info()}>{t('sharedFlows_common:steps.payment.paypal.info')}</Text>
      {status === 'loading' && (
        <Text className={styles.loadingText()}>
          {t('sharedFlows_common:steps.payment.loading')}
        </Text>
      )}
      {status === 'error' && (
        <Text className={styles.errorDetailText()}>
          PayPal is currently unavailable. {errorDetail}
        </Text>
      )}
      <div ref={containerRef} className={styles.mountPoint()} />
    </View>
  )
}

export const StepPayment = ({
  flowName,
  productKey,
  priceData,
  premiumInterval,
  permanentDiscountLost,
}: Props) => {
  const { t } = useTranslation()
  const { language, market } = useLocaleParts()
  const { setValue, getValues } = useFormContext()
  const { track } = useAnalytics()
  const [selected, setSelected] = useState<PaymentType | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paid, setPaid] = useState(false)

  const propsQuery = trpc.paymentFlow.getPaymentProperties.useQuery({ market })
  const finalizeMutation = trpc.paymentFlow.finalizePayment.useMutation()

  const stripeKey = useMemo(() => {
    const d = propsQuery.data
    if (!d) {
      return null
    }
    const methods = d?.payment_methods ?? []
    const stripeMethod = methods.find?.(
      (m: { key?: string; properties?: Record<string, string> }) => m.key === 'stripe_credit_card'
    )
    return stripeMethod?.properties?.stripe_publishable_key ?? d?.stripe_publishable_key ?? null
  }, [propsQuery.data])

  const handleSuccess = useCallback(async () => {
    setError(null)
    setPaid(true)
    setValue('arePaymentDetailsValid', true, { shouldValidate: true })
    if (productKey && premiumInterval && selected) {
      const flowValues = extractTrackingFlowValues(getValues())
      track(
        EVENTS.add_payment_info,
        buildAddPaymentInfoPayload({
          market,
          premiumInterval,
          priceData,
          productKey,
          variant: getTariffLine(flowValues, productKey),
          paymentType: selected,
          email: flowValues.email,
          phoneNumber: flowValues.phone_number,
        })
      )
    }
    await finalizeMutation.mutateAsync({ language, market, flowName })
  }, [
    finalizeMutation,
    flowName,
    getValues,
    language,
    market,
    premiumInterval,
    priceData,
    productKey,
    selected,
    setValue,
    track,
  ])

  const handleError = useCallback((k: string) => setError(k), [])

  const styles = paymentStyles()

  return (
    <View>
      {permanentDiscountLost ? (
        <View className={styles.discountWarning()}>
          <Alert
            variant="warning"
            message={t('sharedFlows_common:steps.payment.permanentDiscountWarning')}
          />
        </View>
      ) : null}
      {error ? (
        <View className={styles.errorBanner()}>
          <Text className={styles.errorText()}>{t(error)}</Text>
        </View>
      ) : null}
      {paid ? (
        <View className={styles.successBanner()}>
          <Text className={styles.successText()}>
            {t('sharedFlows_common:steps.payment.success')}
          </Text>
        </View>
      ) : null}

      <PaymentAccordion
        label={t('sharedFlows_common:generic.inputs.sepa')}
        icons={[{ s: sepaIcon, alt: 'SEPA' }]}
        selected={selected === 'SEPA'}
        onPress={() => setSelected('SEPA')}
      >
        <SepaForm onSuccess={handleSuccess} onError={handleError} flowName={flowName} />
      </PaymentAccordion>

      <PaymentAccordion
        label={t('sharedFlows_common:generic.inputs.cc')}
        icons={[
          { s: visaIcon, alt: 'Visa' },
          { s: mastercardIcon, alt: 'MC' },
          { s: amexIcon, alt: 'Amex' },
        ]}
        selected={selected === 'CREDIT_CARD'}
        onPress={() => setSelected('CREDIT_CARD')}
      >
        <CreditCardForm
          stripeKey={stripeKey}
          onSuccess={handleSuccess}
          onError={handleError}
          loading={loading}
          setLoading={setLoading}
          flowName={flowName}
        />
      </PaymentAccordion>

      <PaymentAccordion
        label="PayPal"
        icons={[{ s: paypalIcon, alt: 'PayPal' }]}
        selected={selected === 'PAYPAL'}
        onPress={() => setSelected('PAYPAL')}
      >
        {selected === 'PAYPAL' && (
          <PayPalSection onSuccess={handleSuccess} onError={handleError} flowName={flowName} />
        )}
      </PaymentAccordion>
    </View>
  )
}
